/**
 * Client mirror of the server's billing constants. MUST stay in sync with
 * [functions/src/billing/config.ts](../../functions/src/billing/config.ts) —
 * a drift between client and server here means the UI shows a different
 * "included" count than what the Stripe invoice charges for, which is
 * the #1 source of billing complaints.
 *
 * The numbers also have to match the Stripe Dashboard configuration:
 *
 *   - `premiumIncludedCredits` MUST equal `up_to` on tier 1 of the
 *     metered overage Price.
 *   - `overagePriceUsdPerCredit` MUST equal `unit_amount / 100` on tier
 *     2 of the metered overage Price.
 *   - `basePriceUsdMonthly` MUST equal `unit_amount / 100` on the
 *     base (licensed) Price.
 *
 * Why duplicate constants on both sides of the wire?
 *
 *   - The server uses them to gate the free quota (Firestore reads
 *     don't go through Stripe).
 *   - The client uses them to render the UsageMeter and PremiumUpsell
 *     copy WITHOUT making a network round trip on every render.
 *
 * Both files are compiled into separate bundles (functions/lib vs
 * dist/), so a normal "shared types" pattern (importing the same .ts)
 * isn't possible without restructuring. Convention: edit both together,
 * and prefer the comment block above as the canonical reference.
 */
export const BILLING_CONFIG = {
  freeMonthlyCredits: 3,
  /** Credits an anonymous (not yet signed-up) session may spend, ever. */
  anonymousCredits: 1,
  /** Anonymous AI calls allowed per IP per UTC day (server-enforced). */
  anonymousDailyPerIp: 5,
  premiumIncludedCredits: 10,
  basePriceUsdMonthly: 3.99,
  overagePriceUsdPerCredit: 0.5,
  currency: "usd",
  /** Shared rooms a free owner can create. Existing rooms are never removed. */
  freeSharedRooms: 1,
  /** Collaborators (besides the owner) a free owner's room can hold. */
  freeRoomCollaborators: 1,
  /** Collaborators (besides the owner) a Premium owner's room can hold. */
  premiumRoomCollaborators: 10,
} as const;

export type BillingConfig = typeof BILLING_CONFIG;

/** Pretty-print a USD amount. `Intl.NumberFormat` with USD will render
 *  $0.50 / $3.99 / $0 correctly. Falls back to a plain `$N.NN` formatter
 *  in environments without `Intl` (very old jsdom / SSR). */
export function formatUsd(
  dollars: number,
  opts?: { hideTrailingZeros?: boolean }
): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits:
        opts?.hideTrailingZeros && dollars % 1 === 0 ? 0 : 2,
    }).format(dollars);
  } catch {
    return `$${dollars.toFixed(2)}`;
  }
}
